import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const PAGE_SIZE_OPTIONS = [10, 20, 30, 40, 50];

interface PagePaginationProps {
  /** What a row is, for the count ("12 intents"). */
  noun: string;
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  onLimitChange: (limit: number) => void;
}

/** Footer for a server-paginated list: the count, rows per page, and first / previous / next / last. */
export function PagePagination({ noun, total, page, limit, totalPages, onPageChange, onLimitChange }: PagePaginationProps) {
  return (
    <div className='flex flex-wrap items-center justify-between gap-3 px-2'>
      <p className='text-sm text-muted-foreground'>
        {total} {noun}
        {total !== 1 ? 's' : ''}
      </p>
      <div className='flex flex-wrap items-center gap-4 lg:gap-8'>
        <div className='flex items-center space-x-2'>
          <p className='text-sm font-medium'>Rows per page</p>
          <Select value={String(limit)} onValueChange={(value) => onLimitChange(Number(value))}>
            <SelectTrigger className='h-8 w-[70px]'>
              <SelectValue placeholder={limit} />
            </SelectTrigger>
            <SelectContent side='top'>
              {PAGE_SIZE_OPTIONS.map((size) => (
                <SelectItem key={size} value={String(size)}>
                  {size}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className='flex w-[100px] items-center justify-center text-sm font-medium'>
          Page {page} of {totalPages}
        </div>
        <div className='flex items-center space-x-2'>
          <Button variant='outline' size='icon' className='hidden size-8 lg:flex' onClick={() => onPageChange(1)} disabled={page <= 1}>
            <span className='sr-only'>Go to first page</span>
            <ChevronsLeft className='size-4' />
          </Button>
          <Button variant='outline' size='icon' className='size-8' onClick={() => onPageChange(page - 1)} disabled={page <= 1}>
            <span className='sr-only'>Go to previous page</span>
            <ChevronLeft className='size-4' />
          </Button>
          <Button variant='outline' size='icon' className='size-8' onClick={() => onPageChange(page + 1)} disabled={page >= totalPages}>
            <span className='sr-only'>Go to next page</span>
            <ChevronRight className='size-4' />
          </Button>
          <Button
            variant='outline'
            size='icon'
            className='hidden size-8 lg:flex'
            onClick={() => onPageChange(totalPages)}
            disabled={page >= totalPages}
          >
            <span className='sr-only'>Go to last page</span>
            <ChevronsRight className='size-4' />
          </Button>
        </div>
      </div>
    </div>
  );
}
